import Trans from 'next-translate/Trans';
import useTranslation from 'next-translate/useTranslation';
import { useRef } from 'react';

import {
  Box,
  ButtonRadio,
  ButtonRadioProps,
  Paper,
  SelectCrossPageContent,
  SelectInline,
  TextPriceTruncated,
  Typography,
} from '@core/component';
import { SelectBaseProps, SelectCrossPageContentProps } from '@core/component/interface';
import { Translate } from '@core/constant';
import { EActionAttribute, EColor, ETypographyVariant, EZIndexName } from '@core/type';
import { ForecastedDeliveryEntity, ForecastedDeliveryProduct } from '@core/type/api';

import { BadgeProductPrice } from './BadgeProductPrice';
import { ProductLogisticBenefit } from './ProductLogisticBenefit';
import { SkeletonForecastedDelivery } from './SkeletonForecastedDelivery';
import { TextProductFees } from './TextProductFees';
import { LOGISTIC_RADIO_TRANSITION } from './constants';
import { ForecastedDeliveryProps, RadioWrapperProps } from './interface-product-show';
import { getDealIndicator } from './utils';

export const RadioDeliveredToCountry = ({
  isActive,
  product,
  selectProps,
  productCount,
  forecastedDeliveryData,
  isForecastedDataLoading,
  ...restProps
}: ForecastedDeliveryProps &
  ButtonRadioProps &
  Pick<RadioWrapperProps, 'isActive' | 'product' | 'productCount' | 'forecastedDeliveryData'> & {
    selectProps: SelectBaseProps;
  }) => {
  const { t: tProductShow } = useTranslation(Translate.page.PRODUCT_SHOW);
  const benefitsRef = useRef<HTMLDivElement>(null);

  const { newProduct: forecastedDeliveryDataProduct, ...restForecastedDeliveryData } =
    forecastedDeliveryData;

  const updatedForecastedDeliveryData: Omit<ForecastedDeliveryEntity, 'newProduct'> & {
    product: ForecastedDeliveryProduct;
  } = {
    ...restForecastedDeliveryData,
    product: forecastedDeliveryDataProduct,
  };

  return (
    <Box width={'350px'}>
      <Paper
        isOutlined
        borderColor={isActive ? EColor.SECONDARY : EColor.PRIMARY}
        zIndex={EZIndexName.OVER_DEFAULT}
        borderRadius={'10px'}
        backgroundColor={isActive ? EColor.INFO : undefined}
        transition={LOGISTIC_RADIO_TRANSITION}
      >
        <ButtonRadio
          wrapperProps={{ padding: '24px', alignItems: 'flex-start' }}
          circleProps={{ marginTop: '7px' }}
          {...restProps}
          label={
            <Box>
              <Box marginBottom={'10px'} display={'flex'} flexWrap={'wrap'} columnGap={'5px'}>
                <Typography variant={ETypographyVariant.H5} display={'inline-block'}>
                  {tProductShow('deliveryTo')}
                </Typography>
                <Box onClick={(e) => e.preventDefault()} marginRight={'-8px'}>
                  <SelectInline
                    display={'inline-block'}
                    iconProps={{ width: '20px', height: '20px' }}
                    Content={SelectCountryContent}
                    {...selectProps}
                  />
                </Box>
              </Box>
              {isForecastedDataLoading ? (
                <SkeletonForecastedDelivery isActive={isActive} />
              ) : (
                <>
                  <BadgeProductPrice
                    isActive={isActive}
                    {...product}
                    {...updatedForecastedDeliveryData}
                    vatPrice={updatedForecastedDeliveryData.product.vat}
                    premiumPrice={updatedForecastedDeliveryData.product.premiumPrice}
                    isDeliveryPrice
                  />

                  <TextProductFees
                    indicator={getDealIndicator(updatedForecastedDeliveryData?.product.indicator)}
                    productType={updatedForecastedDeliveryData?.product.productType}
                  />

                  <Box
                    maxHeight={isActive ? '230px' : '0px'}
                    overflow={'hidden'}
                    transition={LOGISTIC_RADIO_TRANSITION}
                  >
                    <Box
                      ref={benefitsRef}
                      display="flex"
                      flexDirection="column"
                      gap="8px"
                      paddingTop="16px"
                    >
                      <ProductLogisticBenefit title={tProductShow('benefits.allTaxesIncluded')} />
                      <ProductLogisticBenefit title={tProductShow('benefits.insuredAndDiscreet')} />
                      <ProductLogisticBenefit title={tProductShow('benefits.trustedShipping')} />
                    </Box>
                  </Box>

                  <Box display={'flex'} alignItems={'center'} gap={'8px'} marginTop={'8px'}>
                    <Typography textDecoration={'underline'} variant={ETypographyVariant.BODY3}>
                      <Trans
                        i18nKey={`${Translate.page.PRODUCT_SHOW}:benefits.additionalDeliveryCost`}
                        components={{
                          textPrice: (
                            <TextPriceTruncated
                              variant={ETypographyVariant.BODY3}
                              price={forecastedDeliveryData.totalShippingPrice}
                              as={EActionAttribute.SPAN}
                            />
                          ),
                        }}
                      />
                    </Typography>
                  </Box>
                </>
              )}
            </Box>
          }
        />
      </Paper>
    </Box>
  );
};

const SelectCountryContent = ({ children, ...restProps }: SelectCrossPageContentProps) => {
  return (
    <SelectCrossPageContent {...restProps}>
      <Typography variant={ETypographyVariant.H5} marginRight={'4px'} textDecoration={'underline'}>
        {children}
      </Typography>
    </SelectCrossPageContent>
  );
};
